<template>
  <v-app>
    <v-snackbar
      v-model="showSnackbar"
      :timeout="6500"
      :color="snackbar.color"
      :multi-line="mobile || snackbar.multiLine"
      bottom
    >
      {{ snackbar.text }}
      <v-btn color="white" flat @click="showSnackbar = false">Fechar</v-btn>
    </v-snackbar>

    <div v-if="configLoading && !configError" class="config-loading">
      <v-progress-circular
        class="config-loading__loader"
        color="primary"
        indeterminate
      ></v-progress-circular>
    </div>

    <div v-if="configError" class="config-error">
      <div class="config-error__text">{{ enums.responses.error }}</div>
    </div>

    <navbar
      v-if="!(configLoading || configError)"
      :routes="validRoutes"
      :notifications="notifications"
      @logout="logout"
      @notificationClicked="notificationClicked"
      :logoImage="config ? config.images.brandLogo : ''"
    ></navbar>

    <v-content v-if="!(configLoading || configError)" :style="contentStyle">
      <router-view />
    </v-content>

    <template v-if="chatbot.enabled">
      <div v-html="chatbot.content"></div>
    </template>

    <footer-bar v-if="!(configLoading || configError)"></footer-bar>
  </v-app>
</template>

<script>
import './assets/icons/style.css';
import { hasPermission } from '@/utils';
import bus from '@/utils/bus';
import sleep from '@/utils/sleep';
import enums from '@/utils/enums';
import Navbar from '@/components/layout/Navbar';
import docsMixin from '@/mixins/modules/docs';
import FooterBar from '@/components/layout/Footer';
import createTrace from '@/services/logTrace';
import lgpdSecuriti, { setLGPDConsentBanner } from '@/services/lgpdSecuriti';
import requestService from '@/services/request';
import ScreenLoading from '@/components/loaders/ScreenLoading';
// import latePaymentSlip from '@/mixins/modules/latePaymentSlip';
import anticipationPaymentSlip from '@/mixins/modules/anticipationPaymentSlip';
import {
  NOTIFICATIONS,
  UNIT,
  CONFIG,
  APP,
  USER,
  PAYMENT_SLIPS,
} from './utils/constants';
import {
  getToken,
  logout as authServiceLogout,
  setAccessType as setAccessTypeService,
  getAccessType,
  getAdminPreview,
} from '@/services/auth';
import { createNavigationTrace } from '@/router';

export default {
  name: 'App',
  components: {
    Navbar,
    FooterBar,
    ScreenLoading,
  },
  computed: {
    validRoutes() {
      const navbarRoutes = this.$store.getters[APP.NAVBAR_ROUTES];
      const config = this.$store.getters[CONFIG.GET];
      if (!config || !config.navigationRules) return navbarRoutes;
      if (!config.navigationRules.excludeModules) return navbarRoutes;

      return navbarRoutes.filter((route) => {
        const exclude = config.navigationRules.excludeModules.find(
          (item) => route.meta.module === item
        );

        return exclude === undefined;
      });
    },
    notifications() {
      return this.$store.getters[NOTIFICATIONS.GET_ALL];
    },
    config() {
      return this.$store.getters[CONFIG.GET];
    },
    chatbot() {
      return this.$store.getters[CONFIG.GET_CHATBOT];
    },
    configLoading() {
      return this.$store.getters[APP.IS_CONFIG_LOADING];
    },
    hasSegmentations() {
      return this.$store.getters[UNIT.GET_SEGMENTATIONS].length > 0;
    },
    contentStyle() {
      let padding;
      if (
        !this.mobile &&
        this.user &&
        this.selectedUnit &&
        this.hasSegmentations
      ) {
        padding = '98px 0 0';
      } else if (this.mobile) {
        padding = '56px 0 0';
      } else {
        padding = '32px 0 0';
      }
      return { padding };
    },
  },
  data() {
    return {
      snackbar: {
        text: '',
        color: '',
        multiLine: false,
      },
      configError: false,
      showSnackbar: false,
      maxGetConfigAttempts: 3,
    };
  },

  async created() {
    bus.$on('snackbar:open', (data) => {
      const { text, color, multiLine } = data;
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.multiLine = multiLine;
      this.showSnackbar = true;
    });

    bus.$on('unauthorized', () => {
      if (this.$route.meta.requiresAuth) this.logout();
    });

    bus.$on('unit:update', this.onUnitUpdate);
    bus.$on('unit:change', this.onUnitChange);

    await this.init();

    this.applyHomeRule();
    this.showBlockadeWarning();
  },

  mounted() {
    this.setAccessType();
  },

  methods: {
    setAccessType() {
      if (!getToken()) return;

      if (!getAdminPreview()) this.updateUserAccessTime();

      setAccessTypeService(enums.accessTypes.continuedSession);
    },
    applyHomeRule() {
      const hasToGoToHome =
        ![
          'adminPreview',
          'slipUnavailablePage',
          'changeRepeatedEmail',
          'home',
        ].includes(this.$router.currentRoute.name) &&
        getToken() &&
        !sessionStorage.getItem('logged');

      if (hasToGoToHome) {
        this.$router.replace({ name: 'home' });
      }
      sessionStorage.setItem('logged', true);
    },
    showBlockadeWarning() {
      const showBlockadeWarning =
        this.$store.getters[CONFIG.SHOW_BLOCKADE_WARNING];

      if (showBlockadeWarning) {
        this.$router.replace({ name: 'blockadeWarning' });
      }
    },
    onUnitChange(unitObj) {
      if (unitObj && unitObj.product) {
        this.loadNotifications();
      }
    },

    onUnitUpdate(unitObj) {
      this.$store.dispatch(UNIT.SELECT, unitObj);
      if (unitObj && unitObj.product) {
        this.loadNotifications();
      }
      createNavigationTrace(
        this.$router.currentRoute,
        this.$router.currentRoute
      );
    },

    async logout() {
      const { _id, userRole } = this.$store.getters[USER.GET_USER];

      await this.requestService(
        `${process.env.AUTH_API_URL}/updateSession`,
        'POST',
        {
          data: {
            userId: _id,
            userRole,
            action: 'disable',
            admin: this.$store.getters[APP.GET_ADMIN_PREVIEW]
              ? this.$store.getters[APP.GET_ADMIN_PREVIEW]
              : null,
          },
          auth: false,
        }
      );

      authServiceLogout();
      this.$router.replace({ name: 'login' });
    },

    setColors(values) {
      try {
        const root = document.documentElement;
        for (const key in values) {
          root.style.setProperty(`--${key}`, values[key]);
        }
      } catch (err) {
        throw err;
      }
    },

    setDocumentSettings(config) {
      document.title = `${config.brand} - Portal do Cliente`;
      document
        .querySelector('#icon-placeholder')
        .setAttribute('href', config.images.icon);
      document
        .querySelector('#manifest-placeholder')
        .setAttribute('href', config.manifest);
    },

    setAnalytics() {
      window.ga =
        window.ga ||
        function () {
          (ga.q = ga.q || []).push(arguments);
        };
      ga.l = +new Date();
      const integrations = this.$store.getters[CONFIG.GET_SHARED_INTEGRATIONS];
      if (integrations.TRACKING_ID) {
        ga('create', integrations.TRACKING_ID, 'auto');
        ga('require', 'urlChangeTracker');
        ga('send', 'pageview');
      }
      if (integrations.BEXS_TRACKING_ID) {
        ga('create', integrations.BEXS_TRACKING_ID, 'auto', 'bexsAnalytics', {
          tenantId: this.$store.getters[CONFIG.GET_TENANT_ID],
        });
        ga('bexsAnalytics.require', 'urlChangeTracker');
        ga('bexsAnalytics.send', 'pageview');
      }
    },

    async init() {
      if (!this.config) await this.getConfig();

      if (this.isClient && this.units.length > 0) this.loadContracts();
      if (this.selectedUnit) {
        // this.loadNotifications();
        this.$store.dispatch(UNIT.SELECT, this.selectedUnit);
      }
    },

    async loadContracts() {
      try {
        const url = `${process.env.AUTH_API_URL}/user/getContracts`;
        const query = { username: this.user.username, onlyValid: 1, full: 1 };
        const result = await this.requestService(url, 'GET', {
          params: query,
          localErrorHandling: true,
        });
        this.$store.commit(UNIT.MUTATE, result);
        setLGPDConsentBanner(this.user._id);
      } catch (error) {
        console.error(error);
        this.$store.commit(UNIT.MUTATE, []);
        this.$router.push({ name: 'selectUnit' });
      }
    },

    async updateUserAccessTime(i = 1) {
      try {
        if (i > 5) return;

        if (!getToken()) return;

        const { _id, userRole } = this.$store.getters[USER.GET_USER];

        if (!_id || !this.$store.getters[CONFIG.GET]) {
          await sleep(5000);

          return this.updateUserAccessTime();
        }

        const data = {
          accessing: true,
          userRole: userRole,
          user: { _id },
        };

        await requestService(
          `${process.env.AUTH_API_URL}/user/updateUser`,
          'POST',
          {
            data,
          }
        );
      } catch (err) {
        await sleep(5000);

        return this.updateUserAccessTime(++i);
      }
    },

    async getConfig(attempts = 0) {
      try {
        this.$store.commit(APP.LOADING_CONFIG);
        this.configError = false;

        const params = {
          url: window.location.host,
          domain: 'client',
          tenantId: process.env[`DEBUG_TENANT_${process.env.DEBUG_TENANT}`],
        };

        const url = process.env.PUBLIC_CONFIG_API;
        const result = await requestService(url, 'GET', {
          params,
          localErrorHandling: true,
          sendTenant: false,
        });

        if (result) {
          this.$store.commit(CONFIG.SET, result);
          this.$store.commit(APP.LOADED_CONFIG);
          this.setDocumentSettings(result);
          this.setAnalytics(result);
          this.setColors(result.style);
          const sharedIntegrations =
            this.$store.getters[CONFIG.GET_SHARED_INTEGRATIONS] || {};
          lgpdSecuriti(sharedIntegrations);
        }
      } catch (err) {
        if (attempts < this.maxGetConfigAttempts) {
          return this.getConfig(++attempts);
        } else {
          this.configError = true;
          this.$store.commit(APP.LOADED_CONFIG);
        }
      }
    },

    async loadNotifications() {
      try {
        if (
          !this.user ||
          !hasPermission(enums.notificationsProfiles) ||
          !getToken()
        )
          return;

        this.$store.commit(NOTIFICATIONS.LOADING, true);

        const bulletinUrl = `${process.env.GET_BY_ENTERPRISE}/bulletin`;
        const bulletinParams = {
          ...this.getByEnterpriseFromDateParams,
          desiredFields: {
            attachment: 1,
            createdAt: 1,
            lastModified: 1,
            title: 1,
          },
        };
        const visualizedPayload = { ctx: this, username: this.user.username };
        const promises = [
          requestService(bulletinUrl, 'GET', {
            params: bulletinParams,
            localErrorHandling: true,
          }),
          docsMixin.methods.getDocuments.call(this),
          docsMixin.methods.getOwnerDocuments.call(this),
          // this.$store.dispatch(PAYMENT_SLIPS.FETCH_TO_EXPIRE, {
          //   fromNotifications: true,
          // }),
          this.$store.dispatch('lateSlips/getLateSolicitations'),
          // latePaymentSlip.methods.getLateSolicitations.call(this),
          anticipationPaymentSlip.methods.getAnticipationSolicitations.call(
            this
          ),
          this.$store.dispatch(
            NOTIFICATIONS.LOAD_VISUALIZED,
            visualizedPayload
          ),
        ];

        const [bulletins] = await Promise.all(promises);
        this.$store.commit(NOTIFICATIONS.MUTATE_BULLETINS, bulletins);
      } finally {
        this.$store.commit(NOTIFICATIONS.LOADING, false);
      }
    },

    notificationClicked(item) {
      if (!this.user) return;
      item.visualized = true;
      const payload = {
        ctx: this,
        notification: item,
        fromNotifications: true,
      };
      this.$store.dispatch(NOTIFICATIONS.SAVE_VISUALIZED, payload);
    },
  },
};
</script>

<style lang="scss">
@import './assets/scss/breakpoint';

//global styles

* {
  font-family: 'Open Sans', sans-serif;
}

.theme--light.v-input:not(.v-input--is-disabled) {
  & input,
  & textarea {
    color: var(--field-font-color) !important;
  }
}

.input--disabled .v-input__slot {
  background-color: var(--field-disabled-bg-color) !important;
}

.print-logo {
  display: none;
  position: absolute;
  top: 10px;
  right: 10px;
  height: auto;
  width: 225px;
}

@media print {
  @page {
    size: A4;
    margin: 0cm;
  }

  .menu,
  footer,
  video,
  audio,
  object,
  embed,
  .navbar {
    display: none;
  }

  .print-logo {
    display: block;
  }

  .v-toolbar {
    position: relative;
  }

  .v-content {
    padding: 0 !important;
  }

  table.v-table tbody td:first-child,
  table.v-table tbody td:not(:first-child),
  table.v-table tbody th:first-child,
  table.v-table tbody th:not(:first-child),
  table.v-table thead td:first-child,
  table.v-table thead td:not(:first-child),
  table.v-table thead th:first-child,
  table.v-table thead th:not(:first-child) {
    padding: 8px 10px;
    height: initial;
  }

  // .content-block, p, tbody {
  //   page-break-before: auto;
  // }
  .page-footer {
    position: relative !important;
    bottom: 0 !important;
    width: 100% !important;
    padding: 10px !important;
    background-color: white !important;
  }
  .v-tabs__div {
    width: max-content !important;
    > :not(.opaque) {
      display: none;
    }
  }

  .no-print {
    display: none;
  }
}
@media screen {
  .print-only {
    display: none;
  }
}

.table-btn {
  font-size: 12px !important;
  min-width: 50px !important;
  font-weight: 400 !important;
  max-width: 57px !important;
  height: 27px !important;
}

.content {
  width: 100%;
  height: 100%;
}

.config-loading {
  height: 100%;
  width: 100%;
  background: #eeeeee;

  &__loader {
    width: 5% !important;
    height: 32px !important;
    margin: 25% 50% !important;
  }
}

.topic__title {
  padding: 7px 16px 0;
  font-size: 19px;
}

.topic__body {
  padding: 7px 16px 5px;
}

.config-error {
  height: 100%;
  width: 100%;
  background: #eeeeee;

  &__text {
    width: 50%;
    text-align: center;
    margin: 25%;
    font-size: 21px;
  }
}

.empty-state {
  font-size: 14px;
  color: var(--label-color);
  font-weight: 555;
  padding: 1em;
}

.side-text--icon {
  padding: 0 5px;
}

.slin-btn {
  height: 27px !important;
  font-size: 10px !important;

  i {
    padding: 0 0.5em 0 0 !important;
  }
}

.slin-field {
  height: 45px !important;
  font-size: 0.8em !important;
}

.page__tabs {
  .v-tabs__bar {
    background-color: transparent;
  }

  .v-tabs__div {
    font-weight: 400;
    font-size: 1em;
    text-transform: none;
    margin: 0;
    width: max-content;
    max-width: 290px;

    @include breakpoint(mobile) {
      padding: 7.5px 0;
    }

    @media print {
      max-width: initial;
    }
  }

  .v-tabs__container {
    display: flex;

    @include breakpoint(mobile) {
      height: auto;
      display: flex;
      flex-wrap: wrap;
      width: 100%;
    }
  }

  .v-tabs__item {
    color: var(--light-dark-color);
    background-color: #eeeded;
    font-weight: 500;
    border-radius: 5px;
    height: 32.5px !important;
    margin: 0 0.25em;
    opacity: 1 !important;
    @media print {
      margin: 0;
      padding: 0;
    }
  }
}

.pt-headline {
  font-size: 21px !important;
  line-height: 32px !important;
}

.display-1,
.pt-headline {
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  letter-spacing: normal !important;
}

.tooltip {
  &__text {
    color: var(--light-dark-color);
    font-size: 11px;
  }

  &__icon {
    color: #e0e0e0;
    font-weight: 600;
    position: absolute;
  }
}

//modifiers

:root {
  --font-weight: 700;
  --side-margin: 10%;

  --icon-size: 1.5em;
  --font: Arial;
  --label-color: #000;
  --brand-color: #000;
  --border-light-color: #000;
  --box-shadow: 0px 1px 5px #000;
  --light-dark-color: #666;
  --profile-color: #000;
  --financial-color: #000;
  --enterprise-color: #000;
  --support-color: #000;
  --faq-color: #000;
  --docs-color: #000;
  --error-color: #000;
  --bulletin-color: #000;
  --grey-icon-color: #000;
  --card-backdrop-color: #000;
  --field-font-color: #000;
  --field-disabled-bg-color: #000;
  --not-visualized-color: #000;
  --menu-active-color: #000;
  --payment-slip-in-color: #000;
  --payment-slip-in-size: 12px;
  --banner-font-color: #666;
  --login-title-color: #dd2e36;
  --login-subtitle-color: #666;
}

.--required {
  &__label {
    &::after {
      content: '*';
      color: red;
      font-size: 0.9em;
      padding: 0.25em;
    }
  }
  .v-label {
    &::after {
      content: '*';
      color: red;
      font-size: 0.9em;
      padding: 0.25em;
    }
  }
}

.--disabled {
  color: var(--field-font-color) !important;
  background-color: var(--field-disabled-bg-color) !important;
}

.light-dark--color {
  color: var(--light-dark-color);
}

.success--color {
  color: #4bb629 !important;
}

.profile--color {
  color: #5442c2 !important;
}

.financial--color {
  color: #4bb629 !important;
}

.enterprise--color {
  color: #dc2c34 !important;
}

.support--color {
  color: #0093f0 !important;
}

.faq--color {
  color: #0093f0 !important;
}

.docs--color {
  color: #ff7e00 !important;
}

.bulletin--color {
  color: #f958ff !important;
}

.error--color {
  color: #dc2c34 !important;
}

.success--background {
  background: #4bb629 !important;
}

.profile--background {
  background: #5442c2 !important;
}

.financial--background {
  background: #4bb629 !important;
}

.enterprise--background {
  background: #dc2c34 !important;
}

.support--background {
  background: #0093f0 !important;
}

.faq--background {
  background: #0093f0 !important;
}

.docs--background {
  background: #ff7e00 !important;
}

.bulletin--background {
  background: #f958ff !important;
}

.error--background {
  background: #dc2c34 !important;
}

.profile--border-color {
  border-color: #5442c2 !important;
}

.financial--border-color {
  border-color: #4bb629 !important;
}

.enterprise--border-color {
  border-color: #dc2c34 !important;
}

.support--border-color {
  border-color: #0093f0 !important;
}

.faq--border-color {
  border-color: #0093f0 !important;
}

.docs--border-color {
  border-color: #ff7e00 !important;
}

.bulletin--border-color {
  border-color: #f958ff !important;
}

.error--border-color {
  border-color: #dc2c34 !important;
}

.white--text {
  color: #fff;
}

.all--white {
  * {
    color: #fff !important;
  }
}

.center--text {
  text-align: center !important;
}

.bold--text {
  font-weight: var(--font-weight) !important;
}

.opaque {
  opacity: 1 !important;
}

// vuetify's classes overwriting

.v-stepper__step {
  padding: 0 !important;
}

.v-table__overflow {
  @include breakpoint(mobile) {
    overflow-x: hidden;
  }
}

.v-tooltip__content {
  padding: 15px 20px;
}

.v-snack {
  z-index: 10000;
}

.theme--light.v-card {
  color: var(--label-color) !important;
}

.v-input--selection-controls__input {
  input {
    opacity: 1 !important;
    width: 75% !important;
    height: 75% !important;
  }

  i {
    display: none !important;
  }
}

.v-messages__message {
  margin-bottom: 15px;
}

table.v-table thead tr {
  height: 45px;
  font-weight: 700;
}

table.v-table thead th {
  -moz-user-select: none;
  -ms-user-select: none;
  -webkit-user-select: none;
  user-select: none;
  font-size: 14px;
  font-weight: 700;
  background: #f1eeee;
  color: var(--light-dark-color) !important;
}

.v-datatable thead th.column.sortable i {
  font-size: 0.9em !important;
  padding: 0 0.5em;
  margin-left: -1.5em;
}

.theme--light.v-table thead th {
  font-weight: 700;
}

.theme--light.v-table thead tr:first-child {
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  border-bottom: none !important;
}

table.v-table tbody td {
  font-size: 13px;
  font-weight: 400;
  color: var(--light-dark-color);
}

.v-expansion-panel {
  margin: 1em 0;
  border-radius: 3px !important;
  box-shadow: var(--box-shadow) !important;
}

.v-expansion-panel__header {
  transition: 300ms all ease;
}

.v-expansion-panel__container--active > .v-expansion-panel__header {
  background-color: #eeeeee;
}

.theme--light.application {
  background: #fff !important;
}

.theme--light.v-toolbar {
  background-color: #fff !important;
}

.theme--dark.v-card {
  color: var(--label-color) !important;
}

.v-toolbar__content {
  @include breakpoint(not-mobile) {
    height: 100% !important;
  }
}

.theme--dark.v-card,
.v-card {
  background-color: #fff;
  box-shadow: var(--box-shadow) !important;
  border-radius: 3px !important;
}

.v-label {
  font-size: 14px !important;
}

.v-icon {
  font-size: 1.25em;
}

.expansion-icon-size {
  font-size: 1.25em !important;
}

.v-btn {
  padding: 0 16px !important;
  border-radius: 5px !important;
  text-transform: none !important;
  line-height: 1em !important;
  box-shadow: none !important;
  height: 32.5px;

  &.theme--light.normal {
    color: var(--light-dark-color);
    background-color: #eeeded !important;
  }
}

.date-picker .v-btn {
  border-radius: 50% !important;
}

.btn-md {
  height: 32.5px !important;
}

.v-btn--icon {
  padding: 0 !important;
  &:before {
    padding: 20px;
    top: initial !important;
    left: initial !important;
  }
}

.v-overlay {
  z-index: 9999999;
}

.styled-checkbox {
  input {
    display: none;
  }

  .v-input--selection-controls__ripple::after {
    display: block;
    content: '\EA53';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-family: 'bexs-icon' !important;
    color: #0093f0;
  }

  .v-input--selection-controls__ripple.accent--text::after {
    content: '\EA52';
  }
}

//modifiers
.mw-5 {
  width: 5%;
  max-width: 1px;
}
.mw-7 {
  width: 7%;
  max-width: 1px;
}
.mw-10 {
  width: 10%;
  max-width: 1px;
}
.mw-12 {
  width: 12%;
  max-width: 1px;
}
.mw-15 {
  width: 15%;
  max-width: 1px;
}
.mw-20 {
  width: 20%;
  max-width: 2px;
}
.mw-25 {
  width: 25%;
  max-width: 2px;
}
.mw-30 {
  width: 30%;
  max-width: 3px;
}
.mw-35 {
  width: 35%;
  max-width: 3px;
}
.mw-55 {
  width: 55%;
  max-width: 8px;
}

.styled-radio {
  input {
    display: none;
  }

  .v-input--selection-controls__ripple::after {
    content: '';
    display: block;
    width: 18px;
    height: 18px;
    border: 2px solid #0093f0;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border-radius: 50%;
  }

  .v-input--selection-controls__ripple.accent--text::before {
    display: block;
    content: '';
    width: 10px;
    height: 10px;
    background-color: #0093f0;
    opacity: 1;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: scale(1) translate(-50%, -50%);
    transition: none !important;
  }
}
</style>



// WEBPACK FOOTER //
// ./src/App.vue